import dayjs from 'dayjs'
import { match } from 'ts-pattern'
import { z } from 'zod'
import type { MonitorStepPath } from '../consts'
import {
  ALERT_CONFIG_STEP_PATH,
  FILTERS_STEP_PATH,
  KEYWORDS_STEP_PATH,
  MONITOR_TYPES,
  NATURAL_LANGUAGE_STEP_PATH,
  OFFICE_ACTIONS_STEP_PATH,
  PATENT_LIST_STEP_PATH,
  RELEVANCE_OPTIONS,
} from '../consts'
import { datetimeSchema } from './datetime'
import { officeActionEventSchema } from './office-actions'
import { tagSchema } from './tags'

export const monitorTypeSchema = z.enum(MONITOR_TYPES)
export type MonitorType = z.infer<typeof monitorTypeSchema>

export const apiMonitorSchema = z.object({
  open_alert_count: z.number().nonnegative().default(0),
  unread_alert_count: z.number().nonnegative().default(0),
  archived_alert_count: z.number().nonnegative().default(0),
  alert_preview_in_progress: z.boolean().nullable().default(false),
  check_frequency: z.enum(['weekly', 'fortnightly', 'monthly']),
  created_at: datetimeSchema,
  description: z
    .string()
    .nullable()
    .transform((text) => text ?? null),
  effective_from: z.string().nullable(),
  enabled: z.boolean(),
  filters_id: z.string(),
  has_empty_filters: z.boolean(),
  has_empty_keywords: z.boolean(),
  id: z.string(),
  is_draft: z.boolean(),
  is_published: z.boolean(),
  last_check_at: datetimeSchema.nullable(),
  next_check_at: datetimeSchema.nullable(),
  monitored_patents_count: z.number().nonnegative().default(0),
  name: z.string().min(1),
  natural_language_description: z.string(),
  private: z.boolean(),
  relevance_threshold: z.enum(RELEVANCE_OPTIONS),
  type: monitorTypeSchema,
  user_name: z.string(),
  user: z.string(),
  can_enable_office_actions: z.boolean().nullable().default(false),
  monitor_office_action_events: z.array(officeActionEventSchema),
  is_editable: z.boolean(),
  tags: tagSchema.array(),
  is_watchlist: z.boolean(),
})

export const monitorSchema = apiMonitorSchema.transform((monitor) => {
  const {
    open_alert_count: openAlertCount,
    unread_alert_count: unreadAlertCount,
    archived_alert_count: archivedAlertCount,
    alert_preview_in_progress: alertPreviewInProgress,
    check_frequency: checkFrequency,
    created_at: createdAt,
    effective_from: effectiveFrom,
    filters_id: filtersId,
    has_empty_filters: hasEmptyFilters,
    has_empty_keywords: hasEmptyKeywords,
    is_draft: isDraft,
    is_published: isPublished,
    last_check_at: lastCheckAt,
    next_check_at: nextCheckAt,
    monitored_patents_count: monitoredPatentsCount,
    natural_language_description: naturalLanguageDescription,
    private: isPrivate,
    relevance_threshold: relevanceThreshold,
    user_name: userName,
    can_enable_office_actions,
    monitor_office_action_events: monitorOfficeActionEvents,
    is_editable: isEditable,
    is_watchlist: isWatchlist,
    ...rest
  } = monitor

  const canEnableOfficeActions = can_enable_office_actions ?? false

  const finalStepPath = hasEmptyFilters
    ? FILTERS_STEP_PATH
    : ALERT_CONFIG_STEP_PATH

  const lastStepPath = match(monitor.type)
    .returnType<MonitorStepPath>()
    .with('patent', () =>
      monitoredPatentsCount > 0 ? finalStepPath : PATENT_LIST_STEP_PATH
    )
    .with('office_action', () =>
      monitoredPatentsCount > 0
        ? OFFICE_ACTIONS_STEP_PATH
        : PATENT_LIST_STEP_PATH
    )
    .with('natural_language', () =>
      naturalLanguageDescription === ''
        ? NATURAL_LANGUAGE_STEP_PATH
        : finalStepPath
    )
    .with('keyword', () =>
      hasEmptyKeywords ? KEYWORDS_STEP_PATH : finalStepPath
    )
    .with('filter_only', () => finalStepPath)
    .exhaustive()

  let lastAccessibleStep: number
  if (monitor.type === 'filter_only' || monitor.type === 'keyword') {
    lastAccessibleStep = effectiveFrom ? 5 : 4
  } else if (monitor.type === 'office_action' && canEnableOfficeActions) {
    lastAccessibleStep = effectiveFrom ? 7 : 6
  } else {
    lastAccessibleStep = effectiveFrom ? 6 : 5
  }

  const maxAccessibleStep = match(monitor.type)
    .returnType<number>()
    .with('patent', () =>
      monitoredPatentsCount === 0 ? 2 : lastAccessibleStep
    )
    .with('office_action', () =>
      monitoredPatentsCount === 0 ? 2 : lastAccessibleStep
    )
    .with('natural_language', () =>
      naturalLanguageDescription === '' ? 2 : lastAccessibleStep
    )
    .with('keyword', () => (hasEmptyKeywords ? 2 : lastAccessibleStep))
    .with('filter_only', () => (hasEmptyFilters ? 2 : lastAccessibleStep))
    .exhaustive()

  return {
    ...rest,
    openAlertCount,
    unreadAlertCount,
    archivedAlertCount,
    alertPreviewInProgress,
    checkFrequency,
    createdAt,
    effectiveFrom: effectiveFrom
      ? dayjs(effectiveFrom, 'YYYY-MM-DD').toDate()
      : null,
    filtersId,
    hasEmptyFilters,
    hasEmptyKeywords,
    isDraft,
    isPrivate,
    isPublished,
    lastCheckAt,
    nextCheckAt,
    lastStepPath,
    maxAccessibleStep,
    monitoredPatentsCount,
    naturalLanguageDescription,
    relevanceThreshold,
    userName,
    canEnableOfficeActions,
    monitorOfficeActionEvents,
    isEditable,
    isWatchlist,
  }
})

export type Monitor = z.infer<typeof monitorSchema>

export const taggedMonitorSchema = apiMonitorSchema
  .pick({
    id: true,
    name: true,
    created_at: true,
    archived_alert_count: true,
    type: true,
    user_name: true,
    private: true,
    last_check_at: true,
    open_alert_count: true,
    unread_alert_count: true,
    is_editable: true,
  })
  .extend({
    updated_at: z.string(),
    tags: tagSchema.array(),
    user_id: z.string(),
  })

export type TaggedMonitorType = z.infer<typeof taggedMonitorSchema>
